// @ts-nocheck
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

const SAFE_TRIANGLE_PADDING = 10;

// ─── Mobile Menu ───────────────────────────────────────────────────────────────

class MobileMenuComponent extends Component {
  open() {
    this.setAttribute('aria-hidden', 'false');
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.#closeAllAccordions();
  }

  toggleAccordion(event) {
    const btn = event.target;
    const item = btn.closest('.mobile-menu__item');
    const submenu = btn.nextElementSibling;
    const linksList = this.refs.linksList;
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      btn.setAttribute('aria-expanded', 'false');
      submenu.style.maxHeight = '0';
      item.classList.remove('mobile-menu__item--active');
      linksList.classList.remove('mobile-menu__links--has-active');
    } else {
      linksList.querySelectorAll('.mobile-menu__link[aria-expanded="true"]').forEach((otherBtn) => {
        otherBtn.setAttribute('aria-expanded', 'false');
        otherBtn.nextElementSibling.style.maxHeight = '0';
        otherBtn.closest('.mobile-menu__item').classList.remove('mobile-menu__item--active');
      });
      btn.setAttribute('aria-expanded', 'true');
      const styles = getComputedStyle(submenu);
      const padding =
        parseFloat(styles.getPropertyValue('--submenu-padding-top')) +
        parseFloat(styles.getPropertyValue('--submenu-padding-bottom'));
      submenu.style.maxHeight = submenu.scrollHeight + padding + 'px';
      item.classList.add('mobile-menu__item--active');
      linksList.classList.add('mobile-menu__links--has-active');
    }
  }

  toggleSubAccordion(event) {
    const btn = event.target.closest('.mobile-menu__sublink--parent');
    const submenu = btn.nextElementSibling;
    const isExpanded = btn.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      btn.setAttribute('aria-expanded', 'false');
      submenu.style.maxHeight = '0';
    } else {
      const parentSubmenu = btn.closest('.mobile-menu__submenu');
      parentSubmenu.querySelectorAll('.mobile-menu__sublink--parent[aria-expanded="true"]').forEach((otherBtn) => {
        otherBtn.setAttribute('aria-expanded', 'false');
        otherBtn.nextElementSibling.style.maxHeight = '0';
      });
      btn.setAttribute('aria-expanded', 'true');
      const subStyles = getComputedStyle(submenu);
      const subPadding =
        parseFloat(subStyles.getPropertyValue('--sub-submenu-padding-top')) +
        parseFloat(subStyles.getPropertyValue('--sub-submenu-padding-bottom'));
      submenu.style.maxHeight = submenu.scrollHeight + subPadding + 'px';

      const parentMenu = btn.closest('.mobile-menu__submenu');
      if (parentMenu) {
        const parentStyles = getComputedStyle(parentMenu);
        const parentPadding =
          parseFloat(parentStyles.getPropertyValue('--submenu-padding-top')) +
          parseFloat(parentStyles.getPropertyValue('--submenu-padding-bottom'));
        parentMenu.style.maxHeight = parentMenu.scrollHeight + submenu.scrollHeight + subPadding + parentPadding + 'px';
      }
    }
  }

  #closeAllAccordions() {
    const { linksList } = this.refs;
    linksList?.classList.remove('mobile-menu__links--has-active');
    this.querySelectorAll('.mobile-menu__item--active').forEach((item) => {
      item.classList.remove('mobile-menu__item--active');
    });
    this.querySelectorAll('.mobile-menu__link[aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      const submenu = btn.nextElementSibling;
      if (submenu) submenu.style.maxHeight = '0';
    });
    this.querySelectorAll('.mobile-menu__sublink--parent[aria-expanded="true"]').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      const submenu = btn.nextElementSibling;
      if (submenu) submenu.style.maxHeight = '0';
    });
  }
}

// ─── Mega Menu ─────────────────────────────────────────────────────────────────

class MegaMenuComponent extends Component {
  #activeCategory = null;
  #mousePosition = { x: 0, y: 0 };
  #mouseMoveHandler = null;
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.fallback = this.querySelector('[data-mega-fallback]');
    this.parentImage = this.querySelector('[data-mega-parent-image]');
    this.images = Array.from(this.querySelectorAll('[data-mega-link-target]'));
    this.categoryLinks = Array.from(this.querySelectorAll('[data-mega-category]'));
    this.childLists = Array.from(this.querySelectorAll('[data-mega-children-for]'));
    this.childLinks = Array.from(this.querySelectorAll('.mega-menu__child-link'));
    this.childrenEl = this.querySelector('.mega-menu__children');
    this.linksEl = this.querySelector('.mega-menu__links');
    this.safeTriangle = this.querySelector('[data-mega-safe-triangle]');
    this.safeTrianglePath = this.querySelector('[data-mega-safe-triangle-path]');

    const navContainer = this.querySelector('.mega-menu__nav');

    // Category hover — with safe triangle protection
    this.categoryLinks.forEach((link) => {
      link.addEventListener('pointerenter', (e) => {
        // If safe triangle is active, block the switch
        if (this.safeTriangle?.hasAttribute('data-active')) return;
        this.showChildren(e.target);
        this.swapImage(e.target);
      });
    });

    // Leave nav — clear everything
    if (navContainer) {
      navContainer.addEventListener('mouseleave', () => {
        this.#hideSafeTriangle();
        this.clearChildren();
      });
    }

    // Leave links column — show safe triangle if heading toward children, otherwise clear
    if (this.linksEl) {
      this.linksEl.addEventListener('mouseleave', (e) => {
        const hasActiveChildren = this.#activeCategory && this.querySelector('.mega-menu__child-list--active');
        const movingRight = this.childrenEl && e.clientX >= this.linksEl.getBoundingClientRect().right - 5;

        if (hasActiveChildren && movingRight) {
          // Heading toward children — show safe triangle
          this.#showSafeTriangle();
        } else if (!this.childrenEl?.matches(':hover')) {
          // Not heading to children — clear active state
          this.clearChildren();
        }
      });
    }

    // Enter children column — safe arrival, hide triangle
    if (this.childrenEl) {
      this.childrenEl.addEventListener('pointerenter', () => {
        this.#hideSafeTriangle();
      });
    }

    // Safe triangle path events — the core mechanism
    if (this.safeTrianglePath) {
      this.safeTrianglePath.addEventListener('pointerenter', () => {
        // Mouse is inside triangle — do nothing, keep current state
      });
      this.safeTrianglePath.addEventListener('pointerleave', () => {
        this.#hideSafeTriangle();
      });
    }

    // Use event delegation for child links (3rd level image swap)
    if (this.childrenEl) {
      this.childrenEl.addEventListener('mouseover', (e) => {
        const target = e.target.closest('[data-mega-link-trigger]');
        if (target) this.swapImage(target);
      });
    }

    // For panels without children, keep original image swap on all links
    if (this.categoryLinks.length === 0) {
      const allLinks = Array.from(this.querySelectorAll('[data-mega-link-trigger]'));
      allLinks.forEach((link) => {
        link.addEventListener('mouseenter', (e) => this.swapImage(e.target));
      });
    }
  }

  // ── Safe Triangle (2nd → 3rd level) ──────────────────────────────────────────

  #showSafeTriangle() {
    if (!this.safeTriangle || !this.childrenEl) return;
    this.safeTriangle.setAttribute('data-active', '');
    if (!this.#mouseMoveHandler) {
      this.#mouseMoveHandler = (e) => {
        this.#mousePosition = { x: e.clientX, y: e.clientY };
        if (this.#rafId) return;
        this.#rafId = requestAnimationFrame(() => {
          this.#updateSafeTriangle();
          this.#rafId = null;
        });
      };
      window.addEventListener('mousemove', this.#mouseMoveHandler);
    }
  }

  #hideSafeTriangle() {
    if (!this.safeTriangle) return;
    this.safeTriangle.removeAttribute('data-active');
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
    if (this.#mouseMoveHandler) {
      window.removeEventListener('mousemove', this.#mouseMoveHandler);
      this.#mouseMoveHandler = null;
    }
  }

  #updateSafeTriangle() {
    if (!this.safeTriangle || !this.safeTrianglePath || !this.childrenEl) return;

    const childrenRect = this.childrenEl.getBoundingClientRect();
    const { x: mouseX, y: mouseY } = this.#mousePosition;

    // Mouse entered the children area — hide triangle
    if (
      mouseX >= childrenRect.left &&
      mouseX <= childrenRect.right &&
      mouseY >= childrenRect.top &&
      mouseY <= childrenRect.bottom
    ) {
      this.#hideSafeTriangle();
      return;
    }

    // Draw triangle from cursor to children column edges
    const pad = SAFE_TRIANGLE_PADDING;
    const svgWidth = childrenRect.left - mouseX + pad;
    if (svgWidth <= 0) {
      this.#hideSafeTriangle();
      return;
    }

    this.safeTriangle.style.width = `${svgWidth}px`;
    this.safeTriangle.style.height = `${childrenRect.height}px`;
    this.safeTriangle.style.top = `${childrenRect.top}px`;
    this.safeTriangle.style.left = `${mouseX - pad}px`;

    const relativeMouseY = mouseY - childrenRect.top;
    this.safeTrianglePath.setAttribute(
      'd',
      `M 0,${relativeMouseY} L ${svgWidth},0 L ${svgWidth},${childrenRect.height} Z`,
    );
  }

  // ── Panel show/hide ──────────────────────────────────────────────────────────

  show() {
    this.#activeCategory = null;
    this.categoryLinks.forEach((link) => link.classList.remove('mega-menu__link--active'));
    this.childLists.forEach((list) => list.classList.remove('mega-menu__child-list--active'));

    const links = Array.from(this.querySelectorAll('.mega-menu__link'));
    links.forEach((link, i) => {
      link.style.transitionDelay = `${i * 30}ms`;
    });
    this.classList.add('mega-menu__panel--active');
    this.hideAllImages();
    if (this.parentImage) {
      this.parentImage.classList.remove('mega-menu__image-hidden');
    } else {
      this.fallback?.classList.remove('mega-menu__image-hidden');
    }

    // Clear stagger delays after entrance
    const totalDuration = links.length * 30 + 250;
    setTimeout(() => {
      links.forEach((link) => {
        link.style.transitionDelay = '';
      });
    }, totalDuration);
  }

  hide() {
    const links = Array.from(this.querySelectorAll('.mega-menu__link'));
    links.forEach((link) => {
      link.style.transitionDelay = '';
    });
    this.classList.remove('mega-menu__panel--active');
    this.hideAllImages();
    this.#activeCategory = null;
    this.#hideSafeTriangle();
    this.categoryLinks.forEach((link) => link.classList.remove('mega-menu__link--active'));
  }

  showChildren(categoryLink) {
    const url = categoryLink.dataset.megaLinkTrigger;
    if (this.#activeCategory === url) return;
    this.#activeCategory = url;

    this.categoryLinks.forEach((link) => {
      link.classList.toggle('mega-menu__link--active', link.dataset.megaLinkTrigger === url);
    });

    this.childLists.forEach((list) => {
      const isActive = list.dataset.megaChildrenFor === url;
      list.classList.toggle('mega-menu__child-list--active', isActive);
      if (isActive) {
        const links = list.querySelectorAll('.mega-menu__child-link');
        links.forEach((link, i) => {
          link.style.transitionDelay = `${i * 25}ms`;
        });
        const totalDuration = links.length * 25 + 200;
        setTimeout(() => {
          links.forEach((link) => {
            link.style.transitionDelay = '';
          });
        }, totalDuration);
      }
    });
  }

  clearChildren() {
    this.#activeCategory = null;
    this.categoryLinks.forEach((link) => link.classList.remove('mega-menu__link--active'));
    this.childLists.forEach((list) => list.classList.remove('mega-menu__child-list--active'));
    this.hideAllImages();
    if (this.parentImage) {
      this.parentImage.classList.remove('mega-menu__image-hidden');
    } else {
      this.fallback?.classList.remove('mega-menu__image-hidden');
    }
  }

  swapImage(link) {
    const handle = link.dataset.megaLinkTrigger;
    // Exact match first, then fallback to includes for backwards compatibility
    const image =
      this.images.find((img) => img.dataset.megaLinkTarget === handle) ||
      this.images.find((img) => handle.includes(img.dataset.megaLinkTarget));
    this.hideAllImages();
    if (image) {
      image.classList.remove('mega-menu__image-hidden');
    } else if (this.parentImage) {
      this.parentImage.classList.remove('mega-menu__image-hidden');
    } else {
      this.fallback?.classList.remove('mega-menu__image-hidden');
    }
  }

  hideAllImages() {
    this.fallback?.classList.add('mega-menu__image-hidden');
    this.parentImage?.classList.add('mega-menu__image-hidden');
    this.images.forEach((image) => image.classList.add('mega-menu__image-hidden'));
  }
}

// ─── Header ────────────────────────────────────────────────────────────────────

class HeaderComponent extends Component {
  #controller = new AbortController();
  #desktopQuery = window.matchMedia('(min-width: 990px)');
  #activePanel = null;
  #activeNavItem = null;
  #hideTimeout = null;
  #openedViaKeyboard = false;
  #mousePosition = { x: 0, y: 0 };
  #headerMouseHandler = null;
  #headerRafId = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    this.#setupHighlight(signal);
    this.#setupMegaMenus(signal);

    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          if (this.refs.menuToggle?.getAttribute('aria-expanded') === 'true') {
            this.closeMenu();
          }
          const navItemToFocus = this.#activeNavItem?.querySelector('a, button');
          this.#hideActivePanel({ hideHighlight: true });
          navItemToFocus?.focus();
        }
      },
      { signal },
    );

    this.#desktopQuery.addEventListener(
      'change',
      (e) => {
        if (e.matches && this.refs.menuToggle?.getAttribute('aria-expanded') === 'true') {
          this.closeMenu();
        }
      },
      { signal },
    );

    document.addEventListener(
      'click',
      (e) => {
        if (this.#activePanel && !this.#activePanel.contains(e.target) && !e.target.closest('[data-mega-menu]')) {
          this.#hideActivePanel();
        }
      },
      { signal },
    );
  }

  #setupHighlight(signal) {
    const { container } = this.refs;
    const logoWrapper = this.querySelector('.header__logo-wrapper');
    const menuItems = this.querySelectorAll('.apc-header__menu-links li');
    const icons = this.querySelectorAll('.header__icon');
    const allPills = [logoWrapper, ...menuItems, ...icons];

    const pillInners = this.querySelectorAll('.header__pill-inner');

    const updatePillOffsets = () => {
      const cRect = container.getBoundingClientRect();
      pillInners.forEach((inner) => {
        const iRect = inner.getBoundingClientRect();
        inner.style.setProperty('--pill-x', `${iRect.left - cRect.left}px`);
        inner.style.setProperty('--pill-y', `${iRect.top - cRect.top}px`);
      });
    };

    let isVisible = false;

    const moveHighlight = (target) => {
      const rect = target.getBoundingClientRect();
      const cRect = container.getBoundingClientRect();
      allPills.forEach((pill) => pill.classList.remove('header__pill--highlighted'));
      target.classList.add('header__pill--highlighted');
      updatePillOffsets();

      const x = `${rect.left - cRect.left}px`;
      const y = `${rect.top - cRect.top}px`;
      const w = `${rect.width}px`;
      const h = `${rect.height}px`;

      if (!isVisible) {
        // Highlight is hidden — snap to the new position with no slide,
        // then fade opacity in.
        container.classList.add('header__container--snap');
        container.style.setProperty('--hl-x', x);
        container.style.setProperty('--hl-y', y);
        container.style.setProperty('--hl-w', w);
        container.style.setProperty('--hl-h', h);
        // Commit those values while transitions are disabled.
        void container.offsetWidth;
        container.classList.remove('header__container--snap');
        container.style.setProperty('--hl-opacity', '1');
        isVisible = true;
      } else {
        container.style.setProperty('--hl-x', x);
        container.style.setProperty('--hl-y', y);
        container.style.setProperty('--hl-w', w);
        container.style.setProperty('--hl-h', h);
        container.style.setProperty('--hl-opacity', '1');
      }
    };

    let hideHighlightTimeout = null;

    const hideHighlight = () => {
      if (this.#activePanel) return;
      hideHighlightTimeout = setTimeout(() => {
        container.style.setProperty('--hl-opacity', '0');
        allPills.forEach((pill) => pill.classList.remove('header__pill--highlighted'));
        isVisible = false;
      }, 50);
    };

    this._forceHideHighlight = () => {
      clearTimeout(hideHighlightTimeout);
      container.style.setProperty('--hl-opacity', '0');
      allPills.forEach((pill) => pill.classList.remove('header__pill--highlighted'));
      isVisible = false;
    };

    this._hideHighlightIfNotHovering = () => {
      // Check if mouse is currently over any pill element
      const hovered = allPills.some((pill) => pill?.matches(':hover'));
      if (!hovered) {
        clearTimeout(hideHighlightTimeout);
        container.style.setProperty('--hl-opacity', '0');
        allPills.forEach((pill) => pill.classList.remove('header__pill--highlighted'));
        isVisible = false;
      }
    };

    allPills.forEach((pill) => {
      pill.addEventListener(
        'mouseenter',
        () => {
          clearTimeout(hideHighlightTimeout);
          moveHighlight(pill);
          // If this pill is not a mega menu item and a panel is open, close it
          if (this.#activePanel && !pill.closest('[data-mega-menu]')) {
            this.#scheduleHidePanel();
          }
        },
        { signal },
      );
    });

    container.addEventListener(
      'mouseleave',
      () => {
        hideHighlight();
      },
      { signal },
    );
  }

  #setupMegaMenus(signal) {
    const megaPanels = this.querySelectorAll('mega-menu-component');
    if (megaPanels.length === 0) return;

    const megaHandles = new Set();
    megaPanels.forEach((p) => megaHandles.add(p.dataset.megaParent));

    const navItems = this.querySelectorAll('[data-nav-handle]');
    navItems.forEach((li) => {
      if (megaHandles.has(li.dataset.navHandle)) {
        li.setAttribute('data-mega-menu', li.dataset.navHandle);
      }
    });

    const megaMenuItems = this.querySelectorAll('[data-mega-menu]');

    // Nav item hover — open panel, clear safe triangle since we're back in nav
    megaMenuItems.forEach((li) => {
      li.addEventListener(
        'pointerenter',
        () => {
          this.#cancelHidePanel();
          this.#hideHeaderSafeTriangle();
          this.#showPanel(li.dataset.megaMenu, false);
        },
        { signal },
      );
      li.addEventListener('focusin', () => this.#showPanel(li.dataset.megaMenu, true), { signal });
    });

    // Panel hover — keep alive, hide triangle
    megaPanels.forEach((panel) => {
      panel.addEventListener(
        'pointerenter',
        () => {
          this.#cancelHidePanel();
          this.#hideHeaderSafeTriangle();
        },
        { signal },
      );
      panel.addEventListener(
        'pointerleave',
        () => {
          this.#scheduleHidePanel();
          this.#showHeaderSafeTriangle();
        },
        { signal },
      );
    });

    // Close panel and hide highlight when mouse leaves header entirely
    const { container } = this.refs;
    container.addEventListener(
      'mouseleave',
      () => {
        if (this.#activePanel) {
          this.#hideTimeout = setTimeout(() => this.#hideActivePanel({ hideHighlight: true }), 150);
          this.#showHeaderSafeTriangle();
        }
      },
      { signal },
    );

    // Header safe triangle path events
    const triPath = this.refs.headerSafeTrianglePath;
    if (triPath) {
      triPath.addEventListener('pointerenter', () => this.#cancelHidePanel(), { signal });
      triPath.addEventListener(
        'pointerleave',
        () => {
          this.#hideHeaderSafeTriangle();
          this.#scheduleHidePanel();
        },
        { signal },
      );
    }

    // Track mouse position globally while any panel is open
    window.addEventListener(
      'mousemove',
      (e) => {
        this.#mousePosition = { x: e.clientX, y: e.clientY };
        if (this.#activePanel && this.refs.headerSafeTriangle?.hasAttribute('data-active')) {
          if (this.#headerRafId) return;
          this.#headerRafId = requestAnimationFrame(() => {
            this.#updateHeaderSafeTriangle();
            this.#headerRafId = null;
          });
        }
      },
      { signal },
    );
  }

  // ── Header Safe Triangle (1st → 2nd level) ─────────────────────────────────

  #showHeaderSafeTriangle() {
    const { headerSafeTriangle } = this.refs;
    if (!headerSafeTriangle || !this.#activePanel) return;
    headerSafeTriangle.setAttribute('data-active', '');
    this.#updateHeaderSafeTriangle();
  }

  #hideHeaderSafeTriangle() {
    const { headerSafeTriangle } = this.refs;
    if (!headerSafeTriangle) return;
    headerSafeTriangle.removeAttribute('data-active');
  }

  #updateHeaderSafeTriangle() {
    const { headerSafeTriangle, headerSafeTrianglePath } = this.refs;
    if (!headerSafeTriangle || !headerSafeTrianglePath || !this.#activePanel) return;

    const panelRect = this.#activePanel.getBoundingClientRect();
    const { x: mouseX, y: mouseY } = this.#mousePosition;

    // Mouse is inside the panel — no triangle needed
    if (
      mouseY >= panelRect.top &&
      mouseY <= panelRect.bottom &&
      mouseX >= panelRect.left &&
      mouseX <= panelRect.right
    ) {
      headerSafeTriangle.removeAttribute('data-active');
      return;
    }

    // Only draw when mouse is above the panel
    if (mouseY >= panelRect.top) {
      headerSafeTriangle.removeAttribute('data-active');
      return;
    }

    // Only draw if mouse is roughly near the active nav item
    if (this.#activeNavItem) {
      const navRect = this.#activeNavItem.getBoundingClientRect();
      const margin = navRect.width;
      if (mouseX < navRect.left - margin || mouseX > navRect.right + margin) {
        headerSafeTriangle.removeAttribute('data-active');
        return;
      }
    }

    const pad = SAFE_TRIANGLE_PADDING;
    const svgLeft = Math.min(mouseX, panelRect.left) - pad;
    const svgRight = Math.max(mouseX, panelRect.right) + pad;
    const svgTop = mouseY - pad;
    const svgWidth = svgRight - svgLeft;
    const svgHeight = panelRect.top - svgTop + pad;

    headerSafeTriangle.setAttribute('data-active', '');
    headerSafeTriangle.style.width = `${svgWidth}px`;
    headerSafeTriangle.style.height = `${svgHeight}px`;
    headerSafeTriangle.style.top = `${svgTop}px`;
    headerSafeTriangle.style.left = `${svgLeft}px`;

    const cx = mouseX - svgLeft;
    const cy = mouseY - svgTop;
    const pl = panelRect.left - svgLeft;
    const pr = panelRect.right - svgLeft;
    const py = panelRect.top - svgTop;

    headerSafeTrianglePath.setAttribute('d', `M ${cx},${cy} L ${pl},${py} L ${pr},${py} Z`);
  }

  // ── Panel management ─────────────────────────────────────────────────────────

  #showPanel(handle, viaKeyboard = false) {
    this.#cancelHidePanel();
    this.#hideHeaderSafeTriangle();
    if (this.#activePanel) {
      if (this.#openedViaKeyboard) removeTrapFocus();
      this.#activePanel.hide();
    }
    this.#clearActiveNavItem();
    this.#openedViaKeyboard = viaKeyboard;

    const panel = this.querySelector(`mega-menu-component[data-mega-parent="${handle}"]`);
    const navItem = this.querySelector(`[data-mega-menu="${handle}"]`);

    if (panel) {
      panel.show();
      this.#activePanel = panel;
      this.refs.backdrop?.classList.add('mega-menu__backdrop--active');
      if (viaKeyboard) trapFocus(panel);
    }
    if (navItem) {
      navItem.classList.add('mega-menu-active');
      this.#activeNavItem = navItem;
    }
  }

  #hideActivePanel({ hideHighlight = false } = {}) {
    clearTimeout(this.#hideTimeout);
    this.#hideHeaderSafeTriangle();
    if (this.#activePanel) {
      if (this.#openedViaKeyboard) removeTrapFocus();
      this.#activePanel.hide();
      this.#activePanel = null;
      this.#openedViaKeyboard = false;
    }
    this.refs.backdrop?.classList.remove('mega-menu__backdrop--active');
    this.#clearActiveNavItem();
    if (hideHighlight) {
      this._forceHideHighlight?.();
    } else {
      this._hideHighlightIfNotHovering?.();
    }
  }

  #scheduleHidePanel() {
    this.#hideTimeout = setTimeout(() => this.#hideActivePanel(), 150);
  }

  #cancelHidePanel() {
    clearTimeout(this.#hideTimeout);
  }

  #clearActiveNavItem() {
    if (this.#activeNavItem) {
      this.#activeNavItem.classList.remove('mega-menu-active');
      this.#activeNavItem = null;
    }
  }

  toggleMenu() {
    if (this.refs.menuToggle?.getAttribute('aria-expanded') === 'true') {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.refs.menuToggle?.setAttribute('aria-expanded', 'true');
    this.refs.mobileMenu?.open();
    document.body.style.overflow = 'hidden';
    if (this.refs.mobileMenu) trapFocus(this.refs.mobileMenu);
  }

  closeMenu() {
    removeTrapFocus();
    this.refs.menuToggle?.setAttribute('aria-expanded', 'false');
    this.refs.mobileMenu?.close();
    document.body.style.overflow = '';
    this.refs.menuToggle?.focus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }
}

// ─── Register ──────────────────────────────────────────────────────────────────

if (!customElements.get('mobile-menu-component')) customElements.define('mobile-menu-component', MobileMenuComponent);
if (!customElements.get('mega-menu-component')) customElements.define('mega-menu-component', MegaMenuComponent);
if (!customElements.get('header-component')) customElements.define('header-component', HeaderComponent);
